import { useState } from 'react';
import {
  Activity,
  Briefcase,
  Building2,
  CircleHelp,
  Globe,
  Handshake,
  Landmark,
  MessageCircle,
  Phone,
  Store,
  Video,
  type LucideIcon,
} from 'lucide-react';

/**
 * What the page knows about each channel key beyond the API row: the domain
 * whose favicon stands in for it (real brands only), and the tool its cost is read from.
 * Keys follow the channel registry in packages/api/src/modules/gtm-signal-ops/registers.ts.
 */
export const CHANNEL_META: Record<string, { domain?: string; tool?: string }> = {
  'google-ads': { domain: 'google.com', tool: 'Google Ads' },
  'bing-ads': { domain: 'bing.com', tool: 'Microsoft Ads' },
  'linkedin-ads': { domain: 'linkedin.com', tool: 'LinkedIn Campaign Manager' },
  'meta-ads': { domain: 'facebook.com', tool: 'Meta Ads' },
  'email-outbound': { domain: 'apollo.io', tool: 'Apollo + lemlist' },
  'linkedin-outbound': { domain: 'lemlist.com', tool: 'lemlist' },
  seo: { domain: 'google.com' },
  geo: { domain: 'peec.ai', tool: 'Peec AI' },
  newsletter: { domain: 'customer.io', tool: 'Customer.io' },
  'linkedin-organic': { domain: 'linkedin.com' },
  'review-sites': { domain: 'g2.com', tool: 'G2' },
  crm: { domain: 'attio.com' },
};

/** Channels that are not a brand get a plain line icon instead of a lettered box. */
const CHANNEL_GLYPH: Record<string, LucideIcon> = {
  webinar: Video,
  'trade-show': Store,
  'tender-feed': Landmark,
  'job-postings': Briefcase,
  internal: Building2,
  'sdr-calling': Phone,
  'referral-partner': Handshake,
  'word-of-mouth': MessageCircle,
  product: Activity,
  website: Globe,
  unattributed: CircleHelp,
};

/** A 16px brand favicon for real brands, a line icon for generic channels, a monogram as last resort. */
export function ChannelIcon({ channel, label }: { channel: string; label: string }) {
  const domain = CHANNEL_META[channel]?.domain;
  const Glyph = CHANNEL_GLYPH[channel];
  const [failed, setFailed] = useState(false);

  if (domain && !failed) {
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
        alt=""
        width={16}
        height={16}
        className="h-4 w-4 flex-none rounded-[3px]"
        onError={() => setFailed(true)}
      />
    );
  }
  if (Glyph) return <Glyph className="h-4 w-4 flex-none text-text-tertiary" aria-hidden />;
  return (
    <span
      aria-hidden
      className="flex h-4 w-4 flex-none items-center justify-center rounded-[3px] bg-surface-tertiary font-label text-[9px] font-bold uppercase text-text-tertiary"
    >
      {label.trim().charAt(0) || '?'}
    </span>
  );
}
